import { useEffect, useRef, useState } from "react";
import { Volume2, VolumeX } from "lucide-react";
import api from "@/lib/api";

// 2x2 grid geometry shared with StreamRoom (cell 0 = the current room's own video)
// 1v1 battles get taller side-by-side cells; 3-4 host battles use the 2x2 grid.
export const pkCellStyle = (i, count = 4) => {
  const h = count <= 2 ? "30vh" : "21vh";
  return {
    position: "absolute",
    top: i < 2 ? "84px" : `calc(84px + ${h} + 6px)`,
    left: i % 2 === 0 ? "8px" : "calc(50% + 3px)",
    width: "calc(50% - 11px)",
    height: h,
    borderRadius: "14px",
    overflow: "hidden",
    zIndex: 15,
    background: "#101018",
    border: "1px solid rgba(255,255,255,0.14)",
  };
};

function RemoteTile({ entry, score, style }) {
  const boxRef = useRef(null);
  const audioRef = useRef(null);
  const [muted, setMuted] = useState(false);
  const [hasVideo, setHasVideo] = useState(false);
  const mutedRef = useRef(false);

  useEffect(() => {
    if (entry.is_demo || !entry.agora) return;
    let client, stopped = false;
    const run = async () => {
      try {
        const AgoraRTC = (await import("agora-rtc-sdk-ng")).default;
        AgoraRTC.setLogLevel(3);
        client = AgoraRTC.createClient({ mode: "live", codec: "vp8" });
        await client.setClientRole("audience");
        const attach = async (u, mediaType) => {
          try {
            await client.subscribe(u, mediaType);
            if (mediaType === "video" && boxRef.current && u.videoTrack) {
              u.videoTrack.play(boxRef.current, { fit: "cover" });
              setHasVideo(true);
            }
            if (mediaType === "audio" && u.audioTrack) {
              audioRef.current = u.audioTrack;
              if (!mutedRef.current) u.audioTrack.play();
            }
          } catch (e) { /* ignore */ }
        };
        client.on("user-published", attach);
        const a = entry.agora;
        await client.join(a.app_id, a.channel, a.token, a.uid);
        if (stopped) { await client.leave().catch(() => {}); return; }
        for (const u of client.remoteUsers) {
          if (u.hasVideo) await attach(u, "video");
          if (u.hasAudio) await attach(u, "audio");
        }
      } catch (e) {
        console.warn("PK tile join failed", e);
      }
    };
    run();
    return () => {
      stopped = true;
      try { audioRef.current && audioRef.current.stop(); } catch (e) { /* ignore */ }
      audioRef.current = null;
      client && client.leave().catch(() => {});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry.stream_id]);

  const toggleMute = () => {
    const next = !muted;
    mutedRef.current = next;
    setMuted(next);
    const t = audioRef.current;
    try { next ? t && t.stop() : t && t.play(); } catch (e) { /* ignore */ }
  };

  return (
    <div style={style} data-testid={`pk-tile-${entry.host.id}`}>
      {entry.is_demo && entry.video_url ? (
        <video src={entry.video_url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${entry.video_url}` : entry.video_url}
          autoPlay loop muted={muted} playsInline className="w-full h-full object-cover" />
      ) : (
        <div ref={boxRef} className="w-full h-full" />
      )}
      {!entry.is_demo && !hasVideo && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 bg-[#101018]">
          <img src={entry.host.avatar} alt="" className="w-12 h-12 rounded-full object-cover border-2 border-pink-500/60 pulse-live" />
        </div>
      )}
      <div className="absolute bottom-0 inset-x-0 px-1.5 py-1 bg-gradient-to-t from-black/80 to-transparent flex items-center justify-between">
        <span className="text-white text-[9px] font-bold truncate max-w-[60%]">{entry.host.name}</span>
        <span className="text-[#FDE08B] text-[9px] font-extrabold" data-testid={`pk-tile-score-${entry.host.id}`}>{(score || 0).toLocaleString()}</span>
      </div>
      <button data-testid={`pk-tile-mute-${entry.host.id}`} onClick={toggleMute}
        className={`absolute top-1 right-1 w-6 h-6 rounded-full glass-dark flex items-center justify-center ${muted ? "text-red-400" : "text-white"}`}>
        {muted ? <VolumeX size={11} /> : <Volume2 size={11} />}
      </button>
    </div>
  );
}

// Renders the OTHER battling hosts (cells 1..3). Cell 0 is the room's own video, positioned by StreamRoom.
export default function PKGrid({ battle, myStreamId }) {
  const [tokens, setTokens] = useState(null);

  useEffect(() => {
    api.get(`/pk/${battle.id}/watch-tokens`, { params: { exclude: myStreamId } })
      .then(({ data }) => setTokens(data.tokens))
      .catch(() => setTokens([]));
  }, [battle.id, myStreamId]);

  if (!tokens) return null;
  const hostCount = Math.max((battle.hosts || []).filter((h) => h.accepted !== false).length, 2);
  return (
    <div data-testid="pk-grid">
      {tokens.slice(0, 3).map((t, i) => (
        <RemoteTile key={t.stream_id} entry={t} score={battle.scores?.[t.host.id]} style={pkCellStyle(i + 1, hostCount)} />
      ))}
    </div>
  );
}
